import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

const blank = {
  name: '', contactName: '', phone: '', email: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada',
  licenseNumber: '', equipmentType: '', equipmentDetails: '', fleetId: '', startDate: '', status: 'Active', remarks: '',
  authorityType: 'Own Authority', dotNumber: '', mcNumber: '',
  insuranceProvider: '', insurancePolicyNumber: '', insuranceExpiry: '', cargoCoverage: '', liabilityCoverage: '', safetyRating: '',
  payType: '', payValue: '', fuelSurcharge: '', equipmentDeductionPct: '', insuranceDeduction: '', escrowPct: '',
  taxId: '', bankName: '', bankAccount: ''
};

function fromRow(r) {
  if (!r) return blank;
  return {
    name: r.name || '', contactName: r.contact_name || '', phone: r.phone || '', email: r.email || '',
    address: r.address || '', city: r.city || '', province: r.province || '', postalCode: r.postal_code || '', country: r.country || 'Canada',
    licenseNumber: r.license_number || '', equipmentType: r.equipment_type || '', equipmentDetails: r.equipment_details || '',
    fleetId: r.fleet_id || '',
    startDate: r.start_date || '', status: r.status || 'Active', remarks: r.remarks || '',
    authorityType: r.authority_type || 'Own Authority', dotNumber: r.dot_number || '', mcNumber: r.mc_number || '',
    insuranceProvider: r.insurance_provider || '', insurancePolicyNumber: r.insurance_policy_number || '',
    insuranceExpiry: r.insurance_expiry || '', cargoCoverage: r.cargo_coverage ?? '', liabilityCoverage: r.liability_coverage ?? '',
    safetyRating: r.safety_rating || '',
    payType: r.pay_type || '', payValue: r.pay_value ?? '', fuelSurcharge: r.fuel_surcharge || '',
    equipmentDeductionPct: r.equipment_deduction_pct ?? '', insuranceDeduction: r.insurance_deduction ?? '', escrowPct: r.escrow_pct ?? '',
    taxId: r.tax_id || '', bankName: r.bank_name || '', bankAccount: r.bank_account || ''
  };
}

const TABS = ['Basic Information', 'Authority & Insurance', 'Pay Settings', 'Documents'];

export default function OwnerOperatorFormModal({ ownerOperator, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(ownerOperator?.id || null);
  const [form, setForm] = useState(() => fromRow(ownerOperator));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [manualAddress, setManualAddress] = useState(false);
  const [fleets, setFleets] = useState([]);
  useEffect(() => { api('/dispatch/fleets').then(setFleets).catch(() => setFleets([])); }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Documents (tab 3) ────────────────────────────────────────────────────
  const [checklist, setChecklist] = useState(null);
  const loadChecklist = () => { if (savedId) api(`/dispatch/owner-operators/${savedId}/checklist`).then(setChecklist).catch(() => setChecklist([])); };
  useEffect(() => { if (tab === 3 && savedId) loadChecklist(); }, [tab, savedId]);

  const toggleChecklistItem = async (item, isChecked) => {
    await api(`/dispatch/owner-operators/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked, expiryDate: item.expiry_date } });
    loadChecklist();
  };
  const setChecklistExpiry = async (item, expiryDate) => {
    await api(`/dispatch/owner-operators/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked: !!item.is_checked, expiryDate } });
    loadChecklist();
  };
  const addDocType = async () => {
    const name = window.prompt('New document type name');
    if (!name || !name.trim()) return;
    await api('/dispatch/owner-operator-doc-types', { method: 'POST', body: { name, isRequired: false } });
    loadChecklist();
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/owner-operators/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/owner-operators', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => { if (await persist()) setTab(t => Math.min(t + 1, TABS.length - 1)); };
  const reset = () => setForm(ownerOperator ? fromRow(ownerOperator) : blank);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{ownerOperator ? 'Edit owner operator' : 'New owner operator'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Owner Operator Name / Company<br/><input value={form.name} onChange={e => set('name', e.target.value)} /></label>
                <label>Contact Name<br/><input value={form.contactName} onChange={e => set('contactName', e.target.value)} /></label>
                <label>Phone<br/><PhoneInput value={form.phone} onChange={v => set('phone', v)} /></label>
              </div>

              <label>Email<br/><input value={form.email} onChange={e => set('email', e.target.value)} style={{ marginBottom: 18 }} /></label>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>
              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }} onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/><input value={form.city} onChange={e => set('city', e.target.value)} /></label>
                  <label>Province / State<br/><input value={form.province} onChange={e => set('province', e.target.value)} /></label>
                  <label>Postal / Zip<br/><input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} /></label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option><option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Driver's License # (if also driving)<br/><input value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} /></label>
                <label>Equipment Type<br/>
                  <select value={form.equipmentType} onChange={e => set('equipmentType', e.target.value)}>
                    <option value="">—</option>
                    <option>Dry Van</option><option>Flatbed</option><option>Reefer</option><option>Step Deck</option><option>Tanker</option>
                  </select>
                </label>
                <label>Equipment Details<br/><input value={form.equipmentDetails} onChange={e => set('equipmentDetails', e.target.value)} placeholder="e.g. 2022 Freightliner + 53' dry van" /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Fleet (truck/trailer on file)<br/>
                  <select value={form.fleetId} onChange={e => set('fleetId', e.target.value)}>
                    <option value="">—</option>
                    {fleets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Start Date<br/><input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></label>
                <label>Status</label>
              </div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 18, marginTop: -8 }}>
                {['Active', 'Archive'].map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                    <input type="radio" name="oo-status" checked={form.status === s} onChange={() => set('status', s)} style={{ width: 'auto' }} />
                    {s}
                  </label>
                ))}
              </div>

              <label style={{ display: 'block' }}>Remarks<br/><input value={form.remarks} onChange={e => set('remarks', e.target.value)} /></label>
            </>
          )}

          {tab === 1 && (
            <>
              <label>Authority</label>
              <div style={{ display: 'flex', gap: 10, marginBottom: 18, marginTop: 8 }}>
                {['Own Authority', 'Uses Our Authority'].map(a => (
                  <button key={a} type="button" onClick={() => set('authorityType', a)}
                    className={form.authorityType === a ? 'primary-btn' : ''}>
                    {a}
                  </button>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>DOT #<br/><input value={form.dotNumber} onChange={e => set('dotNumber', e.target.value)} /></label>
                <label>MC #<br/><input value={form.mcNumber} onChange={e => set('mcNumber', e.target.value)} /></label>
                <label>Safety Rating<br/>
                  <select value={form.safetyRating} onChange={e => set('safetyRating', e.target.value)}>
                    <option value="">—</option><option>Satisfactory</option><option>Conditional</option><option>Unsatisfactory</option><option>Not Rated</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Insurance Provider<br/><input value={form.insuranceProvider} onChange={e => set('insuranceProvider', e.target.value)} /></label>
                <label>Insurance Policy #<br/><input value={form.insurancePolicyNumber} onChange={e => set('insurancePolicyNumber', e.target.value)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <label>Insurance Expiry<br/><input type="date" value={form.insuranceExpiry} onChange={e => set('insuranceExpiry', e.target.value)} /></label>
                <label>Cargo Coverage ($)<br/><input type="number" value={form.cargoCoverage} onChange={e => set('cargoCoverage', e.target.value)} /></label>
                <label>Liability Coverage ($)<br/><input type="number" value={form.liabilityCoverage} onChange={e => set('liabilityCoverage', e.target.value)} /></label>
              </div>
            </>
          )}

          {tab === 2 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Pay Type<br/>
                  <select value={form.payType} onChange={e => set('payType', e.target.value)}>
                    <option value="">—</option>
                    <option value="% of load">Percentage of Load</option>
                    <option value="per mile">Per Mile</option>
                    <option value="flat rate">Flat Rate</option>
                  </select>
                </label>
                <label>Pay Value<br/><input type="number" value={form.payValue} onChange={e => set('payValue', e.target.value)} placeholder={form.payType === '% of load' ? 'e.g. 75 (%)' : form.payType === 'per mile' ? 'e.g. 0.65 ($/mi)' : 'e.g. 1200 ($)'} /></label>
              </div>

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Fuel Surcharge<br/>
                <select value={form.fuelSurcharge} onChange={e => set('fuelSurcharge', e.target.value)}>
                  <option value="">—</option>
                  <option>Included in pay</option>
                  <option>Paid separately</option>
                  <option>Not applicable</option>
                </select>
              </label>

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>SETTLEMENT DEDUCTIONS</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Equipment Deduction %<br/><input type="number" value={form.equipmentDeductionPct} onChange={e => set('equipmentDeductionPct', e.target.value)} placeholder="if using our trailer" /></label>
                <label>Insurance Deduction ($)<br/><input type="number" value={form.insuranceDeduction} onChange={e => set('insuranceDeduction', e.target.value)} /></label>
                <label>Escrow %<br/><input type="number" value={form.escrowPct} onChange={e => set('escrowPct', e.target.value)} /></label>
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>SETTLEMENT PAYMENT INFO</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <label>Tax ID / EIN<br/><input value={form.taxId} onChange={e => set('taxId', e.target.value)} /></label>
                <label>Bank Name<br/><input value={form.bankName} onChange={e => set('bankName', e.target.value)} /></label>
                <label>Bank Account<br/><input value={form.bankAccount} onChange={e => set('bankAccount', e.target.value)} /></label>
              </div>
            </>
          )}

          {tab === 3 && (
            !savedId ? <div className="empty">Save the owner operator first to track document checklist items.</div>
            : checklist === null ? <div className="empty">Loading...</div>
            : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Check a document once it's in the system and set its expiry. Expired documents auto-uncheck and are flagged as required at dispatch. This list is specific to owner operators.
                </div>
                {checklist.length === 0 && <div className="empty">No document types yet.</div>}
                {checklist.map(item => (
                  <div className="checklist-row" key={item.doc_type_id}>
                    <input type="checkbox" checked={!!item.is_checked} style={{ width: 'auto' }} onChange={e => toggleChecklistItem(item, e.target.checked)} />
                    <div style={{ flex: 1 }}>{item.name}</div>
                    {!!item.is_required && <span className="checklist-req-badge">Required</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700 }}>EXPIRY</span>
                    <input type="date" value={item.expiry_date || ''} style={{ width: 150 }} onChange={e => setChecklistExpiry(item, e.target.value)} />
                  </div>
                ))}
                <button type="button" onClick={addDocType} style={{ marginTop: 14 }}>+ Add document type</button>
              </>
            )
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : ownerOperator ? 'Save changes' : 'Create owner operator'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
